/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIResponseInterface,
  FilterUserInterFace,
  RequestUserInterface,
  SessionUserProperties,
  UserActivityLogs,
  UserInitialProperties,
} from '../../interface/user-management.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { arrayGroupBy, removeEmptyKeys } from '../../shared-functions/DataTime';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import userApi from '../../utils/api/users';

// a thunk to fetch users
export const fetchAllUsers = createAsyncThunk<APIResponseInterface, RequestUserInterface>(
  'user-management/fetchAllUsers',
  async (data: RequestUserInterface, { dispatch }) => {
    try {
      const response = await userApi.fetchUsers(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((userData: any) => ({
          ...userData,
          date: ConvertUTCtoDate(userData.createdAt),
          roleName: userData?.role?.name,
          id: userData._id,
        })),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// Fetch session user
export const fetchSessionUser = createAsyncThunk<SessionUserProperties, string>(
  'user-management/fetchSessionUser',
  async (id: string) => {
    const response = await userApi.fetchUser(id);
    return response.data;
  },
);

// Fetch session user
export const fetchUserActivityLogs = createAsyncThunk<UserActivityLogs[], string>(
  'user-management/fetchUserActivityLogs',
  async (id: string, { dispatch }) => {
    try {
      const response = await userApi.fetchUserActivityLogs(id);
      return response.data.map((userData: any) => ({
        ...userData,
        date: ConvertUTCtoDate(userData.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(userData.createdAt)),
        id: userData._id,
      }));
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'user-management/fetchListFilters',
  async (_, { dispatch }) => {
    try {
      const response = await userApi.fetchListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: UserInitialProperties = {
  users: [],
  userActiveLogs: [],
  sessionUser: {},
  loading: false,
  page: 1,
  limit: 10,
  totalPages: 0,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const userSlice = createSlice({
  name: 'user-management',
  initialState,
  reducers: {
    /**
     * Set User filter data function
     * @param state
     * @param action
     */
    setUserFilterData: (
      state: UserInitialProperties,
      action: PayloadAction<FilterUserInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetUserToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update user filters
     * @param state
     * @param action
     */
    updateUserFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },

  /**
   * Extra reducers functions
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<UserInitialProperties>) => {
    builder
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action: PayloadAction<APIResponseInterface>) => {
        state.loading = false;
        state.users = action.payload.results;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchUserActivityLogs.pending, (state) => {
        state.loading = false;
      })
      .addCase(
        fetchUserActivityLogs.fulfilled,
        (state, action: PayloadAction<UserActivityLogs[]>) => {
          state.loading = false;
          state.userActiveLogs = Object.entries(arrayGroupBy(action.payload, 'date')).map(
            (value) => ({
              logs: value[1].reverse(),
              date: value[0],
            }),
          );
        },
      )
      .addCase(fetchUserActivityLogs.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(
      fetchSessionUser.fulfilled,
      (state, action: PayloadAction<SessionUserProperties>) => {
        state.sessionUser = { ...action.payload, id: action.payload._id };
      },
    );
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { setUserFilterData, resetUserToInitialState, updateUserFilters, setSortData } = userSlice.actions;

// Export the reducer
export default userSlice.reducer;
